export interface PullRequestResult {
  prNumber: number;
  prUrl: string;
  branch: string;
}

export interface PullRequestMetadata {
  number: number;
  html_url: string;
  state: "open" | "closed";
  merged: boolean;
}

export interface PullRequestMergeResult {
  sha: string;
  merged: boolean;
  message: string;
}

export type PullRequestMergeMethod = "merge" | "squash" | "rebase";

interface TranslationPROptions {
  owner: string;
  repo: string;
  locale: string;
  localeName: string;
  translations: any;
  sourcePath: string;
  baseBranch?: string;
  verification?: {
    isComplete: boolean;
    missingKeys: string[];
    totalKeys: number;
  };
}

interface GitHubFileContentResponse {
  sha: string;
  content?: string;
  type: string;
}

interface GitHubRepo {
  id: number;
  name: string;
  owner: {
    login: string;
  };
  description: string | null;
  html_url: string;
  updated_at: string;
  language: string | null;
  stargazers_count: number;
}

class GitHubService {
  private readonly token: string;
  private readonly baseUrl = "https://api.github.com";

  constructor(token: string) {
    this.token = token;
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers: {
        Accept: "application/vnd.github+json",
        Authorization: `Bearer ${this.token}`,
        "X-GitHub-Api-Version": "2022-11-28",
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });

    if (!response.ok) {
      let detail = "";
      try {
        const body = (await response.json()) as { message?: string };
        detail = body?.message ? `: ${body.message}` : "";
      } catch {
        detail = "";
      }
      throw new Error(`GitHub API request failed (${response.status})${detail}`);
    }

    return (await response.json()) as T;
  }

  private utf8ToBase64(value: string): string {
    return window.btoa(unescape(encodeURIComponent(value)));
  }

  private base64ToUtf8(value: string): string {
    return decodeURIComponent(escape(window.atob(value)));
  }

  private generateTranslationFileContent(translations: any, locale: string): string {
    return `// ${locale} translations
// Generated by TranslatorX

export default ${JSON.stringify(translations, null, 2)};
`;
  }

  async createTranslationPR(options: TranslationPROptions): Promise<PullRequestResult> {
    const {
      owner,
      repo,
      locale,
      localeName,
      translations,
      sourcePath,
      baseBranch = "main",
      verification,
    } = options;

    const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const branchName = `translation/${locale}/${date}`;
    const targetPath = sourcePath.replace(/\/[^/]+$/, `/${locale}.js`);
    const content = this.generateTranslationFileContent(translations, locale);

    const refData = await this.request<{ object: { sha: string } }>(
      `/repos/${owner}/${repo}/git/ref/heads/${encodeURIComponent(baseBranch)}`,
    );

    await this.request(
      `/repos/${owner}/${repo}/git/refs`,
      {
        method: "POST",
        body: JSON.stringify({
          ref: `refs/heads/${branchName}`,
          sha: refData.object.sha,
        }),
      },
    );

    let existingFile: GitHubFileContentResponse | null = null;
    try {
      existingFile = await this.request<GitHubFileContentResponse>(
        `/repos/${owner}/${repo}/contents/${encodeURIComponent(
          targetPath,
        )}?ref=${encodeURIComponent(branchName)}`,
      );
    } catch {
      existingFile = null;
    }

    await this.request(
      `/repos/${owner}/${repo}/contents/${encodeURIComponent(targetPath)}`,
      {
        method: "PUT",
        body: JSON.stringify({
          message: `${existingFile ? "Update" : "Add"} ${localeName} (${locale}) translations`,
          content: this.utf8ToBase64(content),
          branch: branchName,
          ...(existingFile?.sha ? { sha: existingFile.sha } : {}),
        }),
      },
    );

    const prTitle = `Add ${localeName} (${locale}) translations`;
    let prBody = `# ${localeName} Translation\n\n`;
    prBody += `This PR adds translations for ${localeName} (${locale}).\n\n`;

    if (verification) {
      const completionPercentage = verification.isComplete
        ? 100
        : Math.round(
            ((verification.totalKeys - verification.missingKeys.length) /
              verification.totalKeys) *
              100,
          );

      prBody += `## Translation Verification\n\n`;
      prBody += `- Translation completion: ${completionPercentage}%\n`;
      prBody += `- Total keys: ${verification.totalKeys}\n`;

      if (!verification.isComplete) {
        prBody += `- Missing keys: ${verification.missingKeys.length}\n\n`;
        if (verification.missingKeys.length > 0) {
          prBody += "### Missing Keys\n```\n";
          verification.missingKeys.forEach((key) => {
            prBody += `${key}\n`;
          });
          prBody += "```\n";
        }
      } else {
        prBody += `\n✅ All keys translated successfully\n`;
      }
    }

    prBody += `\n\n*Generated by TranslatorX*`;

    const prData = await this.request<{ number: number; html_url: string }>(
      `/repos/${owner}/${repo}/pulls`,
      {
        method: "POST",
        body: JSON.stringify({
          title: prTitle,
          body: prBody,
          head: branchName,
          base: baseBranch,
        }),
      },
    );

    return {
      prNumber: prData.number,
      prUrl: prData.html_url,
      branch: branchName,
    };
  }

  async getRepositories(perPage = 100) {
    const data = await this.request<GitHubRepo[]>(
      `/user/repos?sort=updated&visibility=all&per_page=${Math.max(
        1,
        Math.min(100, perPage),
      )}`,
    );

    return data.map((repo) => ({
      id: repo.id,
      name: repo.name,
      owner: {
        login: repo.owner.login,
      },
      description: repo.description,
      html_url: repo.html_url,
      updated_at: repo.updated_at,
      language: repo.language,
      stargazers_count: repo.stargazers_count,
    }));
  }

  async getFileContent(owner: string, repo: string, path: string) {
    const data = await this.request<GitHubFileContentResponse | GitHubFileContentResponse[]>(
      `/repos/${owner}/${repo}/contents/${encodeURIComponent(path)}`,
    );

    if (Array.isArray(data)) {
      throw new Error("Path points to a directory, not a file");
    }

    if (data.type !== "file" || !data.content) {
      throw new Error(`Cannot get content from this resource type: ${data.type}`);
    }

    return this.base64ToUtf8(data.content.replace(/\n/g, ""));
  }

  async getPullRequest(
    owner: string,
    repo: string,
    pullNumber: number,
  ): Promise<PullRequestMetadata> {
    return this.request<PullRequestMetadata>(
      `/repos/${owner}/${repo}/pulls/${pullNumber}`,
    );
  }

  async mergePullRequest(
    owner: string,
    repo: string,
    pullNumber: number,
    method: PullRequestMergeMethod = "squash",
  ): Promise<PullRequestMergeResult> {
    return this.request<PullRequestMergeResult>(
      `/repos/${owner}/${repo}/pulls/${pullNumber}/merge`,
      {
        method: "PUT",
        body: JSON.stringify({
          merge_method: method,
        }),
      },
    );
  }
}

export default GitHubService;
